import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, UserCheck, UserX, Wifi, WifiOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { getAdminAxios } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminAxios().get(`${API}/admin/dashboard`)
      .then(({ data }) => setStats(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-white/5 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-28 bg-white/5 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const cards = [
    { label: "Total Clients", value: stats?.total_clients || 0, icon: Users, color: "text-blue-400" },
    { label: "Active Clients", value: stats?.active_clients || 0, icon: UserCheck, color: "text-emerald-400" },
    { label: "Inactive Clients", value: stats?.inactive_clients || 0, icon: UserX, color: "text-red-400" },
    { label: "Connected WA", value: stats?.connected_sessions || 0, icon: Wifi, color: "text-green-400" },
    { label: "Disconnected WA", value: stats?.disconnected_sessions || 0, icon: WifiOff, color: "text-slate-400" },
  ];

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">Admin Dashboard</h1>
        <p className="text-sm text-slate-400 mt-1">Overview of all clients and WhatsApp sessions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((c) => (
          <Card key={c.label} className="bg-[#13112b] border-indigo-900/30" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <c.icon className={`w-5 h-5 ${c.color}`} />
                <span className="text-2xl font-bold text-white">{c.value}</span>
              </div>
              <p className="text-xs text-slate-400">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-[#13112b] border-indigo-900/30" data-testid="admin-recent-clients">
        <CardHeader>
          <CardTitle className="text-base text-white">Recent Clients</CardTitle>
        </CardHeader>
        <CardContent>
          {(!stats?.recent_clients || stats.recent_clients.length === 0) ? (
            <p className="text-sm text-slate-500 text-center py-6">No clients yet</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_clients.map((client) => (
                <Link key={client.id} to={`/admin/clients/${client.id}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/5 rounded px-2 -mx-2 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-white">{client.name}</p>
                    <p className="text-xs text-slate-400">{client.email}</p>
                  </div>
                  <Badge className={client.is_active ? "bg-emerald-500/20 text-emerald-400 border-0" : "bg-red-500/20 text-red-400 border-0"}>
                    {client.is_active ? "Active" : "Inactive"}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
